import * as React from 'react';
import { observer } from 'mobx-react';
import { FieldState, FormState } from 'formstate';
import * as _ from 'lodash';
import ReactTable from 'react-table';
import Form from 'components/Forms/Form';
import Input from 'components/Forms/Input';
import Textarea from 'components/Forms/Textarea';
import Dropdown, { DropdownOptionInterface } from 'components/Forms/Dropdown';
import { Modal, ModalColumn } from 'components/Modal';
import TableHeader from 'components/Table/TableHeader';
import TabButton from 'components/AdminPage/UserDetailsPage/Contracts/TabButton';
import { required } from 'helpers/FormValidation';
import { canAccessAiPromptAdmin } from 'helpers/AiPromptAccessHelper';
import { getAiModelProviderName, getAiModelProviderOptions } from 'helpers/AiModelProviderHelper';
import { aiPromptStore, AiPromptInterface, AiPromptStatusEnum, AiProviderEnum, languageStore, toastStore } from 'stores';

interface PromptStepGroupInterface {
    stepOrder: number,
    prompts: Array<AiPromptInterface>
}

interface PromptFlowGroupInterface {
    key: string,
    flowKey: string,
    provider: number,
    steps: Array<PromptStepGroupInterface>
}

interface PromptComparisonRowInterface {
    label: string,
    currentValue: string,
    nextValue: string
}

type ModalMode = 'create' | 'edit' | 'copy';
type PromptTab = 'active' | 'draft' | 'inactive';
const purchaseContractFlowKey = 'PurchaseContractExtraction';

const singularPromptPlaceholders = [
    '{company_name}',
    '{vat_id}',
    '{address}',
    '{signer_name}',
    '{signer_email}'
];

const pluralPromptPlaceholders = [
    '{company_names}',
    '{vat_ids}',
    '{addresses}',
    '{signers}'
];

const supportedPromptPlaceholders = [
    ...singularPromptPlaceholders,
    ...pluralPromptPlaceholders
];

const previousExtractedFieldPlaceholders = [
    '{prev.Description}',
    '{prev.DealNumber}',
    '{prev.ContractSum}',
    '{prev.StartDate}',
    '{prev.EndDate}'
];

interface State {
    showModal: boolean,
    modalMode: ModalMode,
    activeTab: PromptTab,
    searchTerm: string,
    flowKeyFilter: string,
    providerFilter: string,
    statusFilter: string,
    promptPendingActivation: AiPromptInterface | null,
    promptPendingHardDelete: AiPromptInterface | null,
    showBulkHardDeleteModal: boolean,
    showInfoModal: boolean
}

@observer class AiPromptsPage extends React.Component<any, State> {

    form = new FormState({
        id: new FieldState<string | null>(null),
        flowKey: new FieldState('').validators(required),
        provider: new FieldState<number>(AiProviderEnum.SCALEWAY).validators(required),
        stepOrder: new FieldState<number | null>(1).validators(required),
        promptKey: new FieldState('').validators(required),
        name: new FieldState('').validators(required),
        description: new FieldState(''),
        template: new FieldState('').validators(required),
        version: new FieldState<number | null>(1).validators(required),
        status: new FieldState<number>(AiPromptStatusEnum.DRAFT)
    })

    bulkHardDeletePhrase = 'DELETE-ALL-ARCHIVED-PROMPTS';

    bulkHardDeleteForm = new FormState({
        confirmKey: new FieldState('').validators(
            required,
            (value: string) => value !== this.bulkHardDeletePhrase && languageStore.get('confirmationPhraseDoesNotMatch')
        )
    })

    constructor(props: any) {
        super(props);

        this.state = {
            showModal: false,
            modalMode: 'create',
            activeTab: 'active',
            searchTerm: '',
            flowKeyFilter: 'all',
            providerFilter: 'all',
            statusFilter: 'all',
            promptPendingActivation: null,
            promptPendingHardDelete: null,
            showBulkHardDeleteModal: false,
            showInfoModal: false
        }
    }

    componentDidMount() {
        aiPromptStore.getPrompts(true);
    }

    resetForm = (values?: Partial<AiPromptInterface>) => {
        const form = this.form.$;

        form.id.reset(values?.id || null);
        form.flowKey.reset(values?.flowKey || purchaseContractFlowKey);
        form.provider.reset(values?.provider || AiProviderEnum.SCALEWAY);
        form.stepOrder.reset(values?.stepOrder || 1);
        form.promptKey.reset(values?.promptKey || '');
        form.name.reset(values?.name || '');
        form.description.reset(values?.description || '');
        form.template.reset(values?.template || '');
        form.version.reset(values?.version || 1);
        form.status.reset(values?.status || AiPromptStatusEnum.DRAFT);
    }

    showCreateModal = (values?: Partial<AiPromptInterface>) => {
        const flowKey = values?.flowKey || purchaseContractFlowKey;
        const provider = values?.provider || AiProviderEnum.SCALEWAY;
        const nextStepOrder = this.getNextStepOrder(flowKey, provider);

        this.resetForm({
            flowKey: flowKey,
            provider: provider,
            stepOrder: values?.stepOrder || nextStepOrder,
            promptKey: values?.promptKey || '',
            name: values?.name || '',
            description: values?.description || '',
            template: values?.template || '',
            version: values?.version || 1,
            status: AiPromptStatusEnum.DRAFT
        });

        this.setState({
            showModal: true,
            modalMode: 'create'
        });
    }

    showEditModal = (prompt: AiPromptInterface) => {
        this.resetForm(prompt);
        this.setState({
            showModal: true,
            modalMode: 'edit'
        });
    }

    showCopyModal = (prompt: AiPromptInterface) => {
        this.resetForm({
            flowKey: prompt.flowKey,
            provider: prompt.provider,
            stepOrder: prompt.stepOrder,
            promptKey: prompt.promptKey,
            name: prompt.name,
            description: prompt.description || '',
            template: prompt.template,
            version: (prompt.version || 0) + 1,
            status: AiPromptStatusEnum.DRAFT
        });

        this.setState({
            showModal: true,
            modalMode: 'copy'
        });
    }

    hideModal = () => {
        this.setState({ showModal: false });
    }

    showActivateModal = (prompt: AiPromptInterface) => {
        this.setState({ promptPendingActivation: prompt });
    }

    hideActivateModal = () => {
        this.setState({ promptPendingActivation: null });
    }

    showHardDeleteModal = (prompt: AiPromptInterface) => {
        this.setState({ promptPendingHardDelete: prompt });
    }

    hideHardDeleteModal = () => {
        this.setState({ promptPendingHardDelete: null });
    }

    openBulkHardDeleteModal = () => {
        this.bulkHardDeleteForm.$.confirmKey.reset('');
        this.setState({ showBulkHardDeleteModal: true });
    }

    closeBulkHardDeleteModal = () => {
        this.setState({ showBulkHardDeleteModal: false });
    }

    openInfoModal = () => {
        this.setState({ showInfoModal: true });
    }

    closeInfoModal = () => {
        this.setState({ showInfoModal: false });
    }

    getProviderOptions(): Array<DropdownOptionInterface> {
        const configuredOptions = getAiModelProviderOptions();
        const currentProvider = this.form.$.provider.$;

        if (currentProvider && !_.find(configuredOptions, { value: currentProvider })) {
            configuredOptions.unshift({
                value: currentProvider,
                name: getAiModelProviderName(currentProvider)
            });
        }

        return configuredOptions;
    }

    getNextStepOrder(flowKey: string, provider: number) {
        const prompts = aiPromptStore.prompts.filter((prompt) => prompt.flowKey === flowKey && prompt.provider === provider);
        if (prompts.length === 0) {
            return 1;
        }

        return _.max(prompts.map((prompt) => prompt.stepOrder))! + 1;
    }

    getContractModuleOptions(): Array<DropdownOptionInterface> {
        return [{
            value: purchaseContractFlowKey,
            name: 'Purchase'
        }];
    }

    getNormalizedKeyParts(value: string) {
        return value
            .trim()
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .split(/[^A-Za-z0-9]+/)
            .filter(Boolean);
    }

    getGeneratedPromptKey(value: string) {
        const parts = this.getNormalizedKeyParts(value);
        if (parts.length === 0) return '';

        return parts
            .map((part) => part.toLowerCase())
            .join('-');
    }

    autofillKeysFromName = () => {
        if (this.state.modalMode === 'edit') {
            return;
        }

        const form = this.form.$;
        const name = form.name.$.trim();
        if (!name) {
            return;
        }

        if (!form.promptKey.$.trim()) {
            form.promptKey.onChange(this.getGeneratedPromptKey(name));
        }
    }

    insertTemplatePlaceholder = (placeholder: string) => {
        const form = this.form.$;
        const currentTemplate = form.template.$ || '';
        const shouldAddSpacer = currentTemplate.length > 0 && !currentTemplate.endsWith('\n') && !currentTemplate.endsWith(' ');
        const nextTemplate = `${currentTemplate}${shouldAddSpacer ? '\n' : ''}${placeholder}`;

        form.template.onChange(nextTemplate);
    }

    getPreviousStepResponsePlaceholders(stepOrder: number | null) {
        const safeStepOrder = Number(stepOrder || 0);
        if (safeStepOrder <= 1) {
            return [] as Array<string>;
        }

        return _.range(1, safeStepOrder).map((step) => `{prev_step_${step}_response}`);
    }

    getPromptFromForm(): AiPromptInterface {
        const form = this.form.$;

        return {
            id: form.id.$,
            flowKey: form.flowKey.$.trim(),
            provider: Number(form.provider.$),
            stepOrder: Number(form.stepOrder.$),
            promptKey: form.promptKey.$.trim(),
            name: form.name.$.trim(),
            description: form.description.$ ? form.description.$.trim() : null,
            template: form.template.$,
            version: Number(form.version.$),
            status: Number(form.status.$)
        };
    }

    onSubmit = async () => {
        const prompt = this.getPromptFromForm();
        const shouldActivate = prompt.status === AiPromptStatusEnum.ACTIVE;
        let promptId = prompt.id || null;

        if (prompt.id && this.state.modalMode === 'edit') {
            prompt.status = AiPromptStatusEnum.DRAFT;
            await aiPromptStore.updatePrompt(prompt);
        }
        else {
            prompt.id = null;
            prompt.status = AiPromptStatusEnum.DRAFT;
            const createdPrompt = await aiPromptStore.createPrompt(prompt);
            promptId = createdPrompt.id || null;
        }

        if (shouldActivate && promptId) {
            await aiPromptStore.activatePrompt(promptId);
        }

        toastStore.addDefaultSuccessfulSaveToast();
        this.hideModal();
    }

    onActivate = (prompt: AiPromptInterface) => {
        this.showActivateModal(prompt);
    }

    onConfirmActivate = async () => {
        const prompt = this.state.promptPendingActivation;
        if (!prompt?.id) return;

        await aiPromptStore.activatePrompt(prompt.id);
        toastStore.addDefaultSuccessfulSaveToast();
        this.hideActivateModal();
    }

    onArchive = async (prompt: AiPromptInterface) => {
        if (!prompt.id) return;

        if (!window.confirm(languageStore.get('areYouSureYouWantToArchivePrompt'))) {
            return;
        }

        await aiPromptStore.archivePrompt(prompt.id);
        toastStore.addDefaultSuccessfulSaveToast();
    }

    onHardDelete = async () => {
        const prompt = this.state.promptPendingHardDelete;
        if (!prompt?.id) return;

        try {
            await aiPromptStore.hardDeletePrompt(prompt.id);
            toastStore.addDefaultSuccessfulSaveToast();
        }
        finally {
            this.hideHardDeleteModal();
        }
    }

    getArchivedPrompts() {
        return aiPromptStore.prompts.filter((prompt) => prompt.status === AiPromptStatusEnum.ARCHIVED);
    }

    onHardDeleteAllArchived = async () => {
        try {
            while (true) {
                const nextArchivedPrompt = this.getArchivedPrompts()[0];
                if (!nextArchivedPrompt?.id) {
                    break;
                }

                await aiPromptStore.hardDeletePrompt(nextArchivedPrompt.id);
            }

            toastStore.addDefaultSuccessfulSaveToast();
        }
        finally {
            this.closeBulkHardDeleteModal();
        }
    }

    getProviderName(provider: number) {
        return getAiModelProviderName(provider);
    }

    getStatusName(status: number) {
        switch (status) {
            case AiPromptStatusEnum.DRAFT:
                return languageStore.get('draft');
            case AiPromptStatusEnum.ACTIVE:
                return languageStore.get('active');
            case AiPromptStatusEnum.ARCHIVED:
                return languageStore.get('archived');
            default:
                return `${languageStore.get('status')} ${status}`;
        }
    }

    getStatusBadge(status: number) {
        let className = 'bg-grey-lighter text-grey-darker';
        if (status === AiPromptStatusEnum.ACTIVE) {
            className = 'bg-green-lightest text-green-dark';
        }
        else if (status === AiPromptStatusEnum.DRAFT) {
            className = 'bg-yellow-lightest text-yellow-darkest border border-yellow-dark';
        }
        else if (status === AiPromptStatusEnum.ARCHIVED) {
            className = 'bg-red-lightest text-red-dark';
        }

        return (
            <span className={`inline-block py-1 px-2 rounded text-xs font-semibold ${className}`}>
                {this.getStatusName(status)}
            </span>
        );
    }

    getCurrentActivePrompt(flowKey: string, provider: number, stepOrder: number, excludedPromptId?: string | null) {
        return _.find(aiPromptStore.prompts, (prompt) =>
            prompt.flowKey === flowKey &&
            prompt.provider === provider &&
            prompt.stepOrder === stepOrder &&
            prompt.status === AiPromptStatusEnum.ACTIVE &&
            prompt.id !== excludedPromptId
        ) || null;
    }

    getPromptsForTab(activeTab: PromptTab) {
        return aiPromptStore.prompts.slice().filter((prompt) => {
            if (activeTab === 'active') {
                return prompt.status === AiPromptStatusEnum.ACTIVE;
            }

            if (activeTab === 'draft') {
                return prompt.status === AiPromptStatusEnum.DRAFT;
            }

            return prompt.status === AiPromptStatusEnum.ARCHIVED;
        });
    }

    getFilteredPromptsForTab(activeTab: PromptTab) {
        const searchTerm = this.state.searchTerm.trim().toLowerCase();

        return this.getPromptsForTab(activeTab).filter((prompt) => {
            if (this.state.flowKeyFilter !== 'all' && prompt.flowKey !== this.state.flowKeyFilter) {
                return false;
            }

            if (this.state.providerFilter !== 'all' && String(prompt.provider) !== this.state.providerFilter) {
                return false;
            }

            if (this.state.statusFilter !== 'all' && String(prompt.status) !== this.state.statusFilter) {
                return false;
            }

            if (!searchTerm) {
                return true;
            }

            const searchableValues = [
                prompt.flowKey,
                prompt.promptKey,
                prompt.name,
                prompt.description || '',
                prompt.template
            ];

            return searchableValues.some((value) => value.toLowerCase().includes(searchTerm));
        });
    }

    getGroupedPrompts(activeTab: PromptTab): Array<PromptFlowGroupInterface> {
        const groupedByFlowAndProvider = _.groupBy(this.getFilteredPromptsForTab(activeTab), (prompt) => `${prompt.flowKey}__${prompt.provider}`);

        return _.orderBy(Object.keys(groupedByFlowAndProvider).map((key) => {
            const prompts = groupedByFlowAndProvider[key];
            const [firstPrompt] = prompts;
            const groupedByStep = _.groupBy(prompts, (prompt) => prompt.stepOrder);

            return {
                key: key,
                flowKey: firstPrompt.flowKey,
                provider: firstPrompt.provider,
                steps: _.orderBy(Object.keys(groupedByStep).map((stepOrder) => ({
                    stepOrder: Number(stepOrder),
                    prompts: _.orderBy(groupedByStep[stepOrder], ['version'], ['desc'])
                })), ['stepOrder'], ['asc'])
            };
        }), ['flowKey', 'provider'], ['asc', 'asc']);
    }

    setActiveTab = (activeTab: PromptTab) => {
        this.setState({
            activeTab,
            statusFilter: 'all'
        });
    }

    clearFilters = () => {
        this.setState({
            searchTerm: '',
            flowKeyFilter: 'all',
            providerFilter: 'all',
            statusFilter: 'all'
        });
    }

    getFlowKeyFilterOptions() {
        return _.uniq(aiPromptStore.prompts.map((prompt) => prompt.flowKey)).sort();
    }

    getProviderFilterOptions() {
        return _.sortBy(
            _.uniq(aiPromptStore.prompts.map((prompt) => prompt.provider)).map((provider) => ({
                value: String(provider),
                name: this.getProviderName(provider)
            })),
            'name'
        );
    }

    getStatusFilterOptions() {
        const options = [{
            value: 'all',
            label: languageStore.get('all')
        }];

        if (this.state.activeTab === 'active') {
            options.push({
                value: String(AiPromptStatusEnum.ACTIVE),
                label: this.getStatusName(AiPromptStatusEnum.ACTIVE)
            });
            return options;
        }

        if (this.state.activeTab === 'draft') {
            options.push({
                value: String(AiPromptStatusEnum.DRAFT),
                label: this.getStatusName(AiPromptStatusEnum.DRAFT)
            });
            return options;
        }

        options.push({
            value: String(AiPromptStatusEnum.ARCHIVED),
            label: this.getStatusName(AiPromptStatusEnum.ARCHIVED)
        });

        return options;
    }

    getModalStatusOptions(): Array<DropdownOptionInterface> {
        return [
            {
                value: AiPromptStatusEnum.DRAFT,
                name: this.getStatusName(AiPromptStatusEnum.DRAFT)
            },
            {
                value: AiPromptStatusEnum.ACTIVE,
                name: this.getStatusName(AiPromptStatusEnum.ACTIVE)
            }
        ];
    }

    getGroupPrompts(flowKey: string, provider: number) {
        return aiPromptStore.prompts.filter((prompt) => prompt.flowKey === flowKey && prompt.provider === provider);
    }

    getMissingActiveStepOrders(flowKey: string, provider: number) {
        const prompts = this.getGroupPrompts(flowKey, provider);
        const maxStepOrder = _.max(prompts.map((prompt) => prompt.stepOrder)) || 0;
        const activeStepOrders = _.uniq(prompts
            .filter((prompt) => prompt.status === AiPromptStatusEnum.ACTIVE)
            .map((prompt) => prompt.stepOrder));

        return _.range(1, maxStepOrder + 1).filter((stepOrder) => !activeStepOrders.includes(stepOrder));
    }

    getActiveStepCount(flowKey: string, provider: number) {
        return _.uniq(this.getGroupPrompts(flowKey, provider)
            .filter((prompt) => prompt.status === AiPromptStatusEnum.ACTIVE)
            .map((prompt) => prompt.stepOrder)).length;
    }

    formatPromptValue(value: string | number | null | undefined) {
        if (value === null || value === undefined || value === '') {
            return '-';
        }

        return String(value);
    }

    getActivationComparisonRows(prompt: AiPromptInterface, currentActivePrompt: AiPromptInterface | null): Array<PromptComparisonRowInterface> {
        const rows = [
            {
                label: languageStore.get('promptKey'),
                currentValue: this.formatPromptValue(currentActivePrompt?.promptKey),
                nextValue: this.formatPromptValue(prompt.promptKey)
            },
            {
                label: languageStore.get('name'),
                currentValue: this.formatPromptValue(currentActivePrompt?.name),
                nextValue: this.formatPromptValue(prompt.name)
            },
            {
                label: languageStore.get('description'),
                currentValue: this.formatPromptValue(currentActivePrompt?.description),
                nextValue: this.formatPromptValue(prompt.description)
            },
            {
                label: languageStore.get('version'),
                currentValue: this.formatPromptValue(currentActivePrompt?.version),
                nextValue: this.formatPromptValue(prompt.version)
            }
        ];

        return rows.filter((row) => !currentActivePrompt || row.currentValue !== row.nextValue);
    }

    getTableColumns() {
        return [
            {
                Header: <TableHeader value={languageStore.get('version')} />,
                id: 'version',
                width: 90,
                accessor: (prompt: AiPromptInterface) => prompt.version
            },
            {
                Header: <TableHeader value={languageStore.get('promptKey')} />,
                accessor: 'promptKey'
            },
            {
                Header: <TableHeader value={languageStore.get('name')} />,
                accessor: 'name'
            },
            {
                Header: <TableHeader value={languageStore.get('description')} />,
                id: 'description',
                accessor: (prompt: AiPromptInterface) => prompt.description || '-',
                Cell: (props: any) => (
                    <div className='text-xs leading-normal whitespace-pre-wrap' title={props.value}>
                        {props.value}
                    </div>
                )
            },
            {
                Header: <TableHeader value={languageStore.get('status')} />,
                id: 'status',
                width: 120,
                accessor: (prompt: AiPromptInterface) => prompt.status,
                Cell: (props: any) => this.getStatusBadge(props.value)
            },
            {
                Header: <TableHeader value={languageStore.get('promptFieldLabel')} />,
                id: 'template',
                accessor: (prompt: AiPromptInterface) => prompt.template,
                Cell: (props: any) => (
                    <div className='text-xs leading-normal whitespace-pre-wrap' title={props.value}>
                        {props.value}
                    </div>
                )
            },
            {
                Header: <TableHeader value={languageStore.get('actions')} />,
                id: 'actions',
                width: 320,
                sortable: false,
                accessor: (prompt: AiPromptInterface) => prompt.id,
                Cell: (props: any) => {
                    const prompt = props.original as AiPromptInterface;
                    const canEdit = prompt.status === AiPromptStatusEnum.DRAFT;
                    const canActivate = prompt.status !== AiPromptStatusEnum.ACTIVE;
                    const canArchive = prompt.status !== AiPromptStatusEnum.ARCHIVED;
                    const canHardDelete = prompt.status === AiPromptStatusEnum.ARCHIVED;

                    return (
                        <div className='flex flex-wrap'>
                            {
                                canEdit &&
                                <button
                                    className='border rounded px-3 py-1 mr-2 mb-2 hover:bg-grey-lightest'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.showEditModal(prompt);
                                    }}>
                                    {languageStore.get('edit')}
                                </button>
                            }

                            <button
                                className='border rounded px-3 py-1 mr-2 mb-2 hover:bg-grey-lightest'
                                onClick={(e) => {
                                    e.preventDefault();
                                    this.showCopyModal(prompt);
                                }}>
                                {languageStore.get('copyToDraft')}
                            </button>

                            {
                                canActivate &&
                                <button
                                    className='bg-comablue-label text-white rounded px-3 py-1 mr-2 mb-2'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.onActivate(prompt);
                                    }}>
                                    {languageStore.get('activate')}
                                </button>
                            }

                            {
                                canArchive &&
                                <button
                                    className='bg-red-dark text-white rounded px-3 py-1 mb-2'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.onArchive(prompt);
                                    }}>
                                    {languageStore.get('archive')}
                                </button>
                            }

                            {
                                canHardDelete &&
                                <button
                                    className='bg-red-dark text-white rounded px-3 py-1 ml-2 mb-2'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.showHardDeleteModal(prompt);
                                    }}>
                                    {languageStore.get('deletePermanently')}
                                </button>
                            }
                        </div>
                    );
                }
            }
        ];
    }

    getModalTitle() {
        switch (this.state.modalMode) {
            case 'edit':
                return languageStore.get('editPrompt');
            case 'copy':
                return languageStore.get('copyToDraft');
            default:
                return languageStore.get('createPrompt');
        }
    }

    renderStepSection(stepGroup: PromptStepGroupInterface, flowKey: string, provider: number) {
        const activePrompt = this.getCurrentActivePrompt(flowKey, provider, stepGroup.stepOrder);

        return (
            <div key={stepGroup.stepOrder} className='mb-8 last:mb-0'>
                <div className='flex justify-between items-center mb-3'>
                    <div>
                        <h3 className='mb-1'>{`${languageStore.get('stepOrder')} ${stepGroup.stepOrder}`}</h3>
                        <div className={`text-sm ${activePrompt ? 'text-grey-darker' : 'text-red-dark font-semibold'}`}>
                            {
                                activePrompt
                                    ? `${languageStore.get('activePrompt')}: v${activePrompt.version} ${activePrompt.name}`
                                    : languageStore.get('noActivePrompt')
                            }
                        </div>
                    </div>

                    <button
                        onClick={() => this.showCreateModal({
                            flowKey: stepGroup.prompts[0].flowKey,
                            provider: stepGroup.prompts[0].provider,
                            stepOrder: stepGroup.stepOrder,
                            promptKey: stepGroup.prompts[0].promptKey,
                            version: (stepGroup.prompts[0].version || 0) + 1
                        })}
                        className='bg-grey-dark text-white py-2 px-4 rounded'>
                        {languageStore.get('addPrompt')}
                    </button>
                </div>

                <div className='border rounded p-4'>
                    <ReactTable
                        data={stepGroup.prompts.slice()}
                        columns={this.getTableColumns()}
                        defaultSorted={[{ id: 'version', desc: true }]}
                        defaultPageSize={stepGroup.prompts.length || 1}
                        showPagination={false}
                        NoDataComponent={() => null}
                        minRows={0} />
                </div>
            </div>
        );
    }

    renderGroup(group: PromptFlowGroupInterface) {
        const missingActiveSteps = this.getMissingActiveStepOrders(group.flowKey, group.provider);
        const activeStepCount = this.getActiveStepCount(group.flowKey, group.provider);

        return (
            <div key={group.key} className='border rounded p-5 mb-8'>
                <div className='mb-6'>
                    <div>
                        <h2 className='mb-1'>{group.flowKey}</h2>
                        <div className='text-sm text-grey-darker'>
                            {`${languageStore.get('provider')}: ${this.getProviderName(group.provider)}`}
                        </div>
                    </div>
                </div>

                {
                    missingActiveSteps.length > 0
                        ? (
                            <div className='mb-6 rounded border border-yellow-dark px-4 py-3 bg-yellow-lightest text-yellow-darkest'>
                                {`${languageStore.get('missingActiveSteps')}: ${missingActiveSteps.join(', ')}`}
                            </div>
                        )
                        : (
                            <div className='mb-6 rounded border px-4 py-3 bg-green-lightest text-green-dark'>
                                {`${languageStore.get('flowReadyWithActiveSteps')} ${activeStepCount}`}
                            </div>
                        )
                }

                {group.steps.map((stepGroup) => this.renderStepSection(stepGroup, group.flowKey, group.provider))}
            </div>
        );
    }

    render() {
        if (!canAccessAiPromptAdmin()) {
            return null;
        }

        const groups = this.getGroupedPrompts(this.state.activeTab);
        const form = this.form.$;
        const activePromptCount = this.getPromptsForTab('active').length;
        const draftPromptCount = this.getPromptsForTab('draft').length;
        const inactivePromptCount = this.getPromptsForTab('inactive').length;
        const currentTabPromptCount = this.getPromptsForTab(this.state.activeTab).length;
        const archivedCount = this.getArchivedPrompts().length;
        const contractModuleOptions = this.getContractModuleOptions();
        const providerOptions = this.getProviderOptions();
        const modalStatusOptions = this.getModalStatusOptions();
        const flowKeyFilterOptions = this.getFlowKeyFilterOptions();
        const providerFilterOptions = this.getProviderFilterOptions();
        const statusFilterOptions = this.getStatusFilterOptions();
        const previousStepResponsePlaceholders = this.getPreviousStepResponsePlaceholders(form.stepOrder.$);
        const activationPrompt = this.state.promptPendingActivation;
        const currentActivePrompt = activationPrompt
            ? this.getCurrentActivePrompt(activationPrompt.flowKey, activationPrompt.provider, activationPrompt.stepOrder, activationPrompt.id)
            : null;
        const activationComparisonRows = activationPrompt
            ? this.getActivationComparisonRows(activationPrompt, currentActivePrompt)
            : [];

        return (
            <div className='mt-10 mx-auto'>
                <div className='mb-5 flex justify-between items-center'>
                    <div>
                        <TabButton
                            className='mr-2'
                            label={`${languageStore.get('active')} (${activePromptCount})`}
                            isActive={this.state.activeTab === 'active'}
                            onClick={() => this.setActiveTab('active')} />
                        <TabButton
                            className='mr-2'
                            label={`${languageStore.get('draft')} (${draftPromptCount})`}
                            isActive={this.state.activeTab === 'draft'}
                            onClick={() => this.setActiveTab('draft')} />
                        <TabButton
                            label={`${languageStore.get('inactive')} (${inactivePromptCount})`}
                            isActive={this.state.activeTab === 'inactive'}
                            onClick={() => this.setActiveTab('inactive')} />
                    </div>

                    <div className='flex'>
                        {
                            this.state.activeTab === 'inactive' && archivedCount > 0 &&
                            <button
                                onClick={this.openBulkHardDeleteModal}
                                className='bg-red-dark text-white py-2 px-4 rounded mr-2'>
                                {languageStore.get('deleteAllArchivedPermanently')}
                            </button>
                        }

                        <button onClick={() => this.showCreateModal()} className='bg-comablue-label text-white py-2 px-8 rounded'>
                            {languageStore.get('addPrompt')}
                        </button>
                    </div>
                </div>

                <div className='border rounded p-4 mb-6 bg-grey-lightest'>
                    <div className='mb-3 text-sm text-grey-darker'>{languageStore.get('filters')}</div>
                    <div className='flex flex-wrap items-end'>
                        <div className='mr-4 mb-3' style={{ minWidth: '260px' }}>
                            <label className='block text-comablue-label text-sm mb-1'>
                                {languageStore.get('search')}
                            </label>
                            <input
                                value={this.state.searchTerm}
                                onChange={(e) => this.setState({ searchTerm: e.target.value })}
                                placeholder={languageStore.get('searchPrompts')}
                                className='w-full border rounded py-2 px-2 bg-white'
                                type='text' />
                        </div>

                        <div className='mr-4 mb-3' style={{ minWidth: '220px' }}>
                            <label className='block text-comablue-label text-sm mb-1'>
                                {languageStore.get('flowKey')}
                            </label>
                            <select
                                value={this.state.flowKeyFilter}
                                onChange={(e) => this.setState({ flowKeyFilter: e.target.value })}
                                className='w-full border rounded py-2 px-2 bg-white'>
                                <option value='all'>{languageStore.get('all')}</option>
                                {
                                    flowKeyFilterOptions.map((flowKey) => (
                                        <option value={flowKey} key={flowKey}>{flowKey}</option>
                                    ))
                                }
                            </select>
                        </div>

                        <div className='mr-4 mb-3' style={{ minWidth: '180px' }}>
                            <label className='block text-comablue-label text-sm mb-1'>
                                {languageStore.get('provider')}
                            </label>
                            <select
                                value={this.state.providerFilter}
                                onChange={(e) => this.setState({ providerFilter: e.target.value })}
                                className='w-full border rounded py-2 px-2 bg-white'>
                                <option value='all'>{languageStore.get('all')}</option>
                                {
                                    providerFilterOptions.map((option) => (
                                        <option value={option.value} key={option.value}>{option.name}</option>
                                    ))
                                }
                            </select>
                        </div>

                        <div className='mr-4 mb-3' style={{ minWidth: '180px' }}>
                            <label className='block text-comablue-label text-sm mb-1'>
                                {languageStore.get('status')}
                            </label>
                            <select
                                value={this.state.statusFilter}
                                onChange={(e) => this.setState({ statusFilter: e.target.value })}
                                className='w-full border rounded py-2 px-2 bg-white'>
                                {
                                    statusFilterOptions.map((option) => (
                                        <option value={option.value} key={option.value}>{option.label}</option>
                                    ))
                                }
                            </select>
                        </div>

                        <button
                            onClick={this.clearFilters}
                            className='mb-3 border rounded py-2 px-4 hover:bg-white'>
                            {languageStore.get('clearFilters')}
                        </button>
                    </div>
                </div>

                {
                    groups.length === 0 &&
                    <div className='border rounded p-5 text-grey-darker'>
                        {
                            currentTabPromptCount > 0
                                ? languageStore.get('noPromptsMatchFilters')
                                : languageStore.get('noPromptsFound')
                        }
                    </div>
                }

                {groups.map((group) => this.renderGroup(group))}

                {
                    this.state.showModal &&
                    <Form onValidSubmit={this.onSubmit} formState={this.form}>
                        <Modal
                            title={this.getModalTitle()}
                            forceWide={true}
                            primaryButton={{ title: languageStore.get('saveChanges') }}
                            secondaryButton={{ title: languageStore.get('cancel'), onClick: this.hideModal }}>

                            <ModalColumn>
                                <Input
                                    fieldState={form.name}
                                    id='name'
                                    label={languageStore.get('name')}
                                    onChange={this.autofillKeysFromName}
                                    onBlur={this.autofillKeysFromName} />

                                <Dropdown
                                    fieldState={form.flowKey}
                                    id='flowKey'
                                    label={languageStore.get('contractModule')}
                                    options={contractModuleOptions}
                                    disabled={true} />

                                <Dropdown
                                    fieldState={form.provider}
                                    id='provider'
                                    label={languageStore.get('provider')}
                                    type='number'
                                    options={providerOptions}
                                    disabled={providerOptions.length <= 1} />

                                <Input
                                    type='number'
                                    fieldState={form.stepOrder}
                                    id='stepOrder'
                                    label={languageStore.get('stepOrder')} />

                                <Input
                                    fieldState={form.promptKey}
                                    id='promptKey'
                                    label={languageStore.get('promptKey')} />
                                <div className='-mt-6 mb-8 text-xs text-grey-darker'>
                                    {languageStore.get('promptKeyAutoGeneratedHint')}
                                </div>

                                <Textarea
                                    fieldState={form.description}
                                    id='description'
                                    label={languageStore.get('description')} />

                                <Input
                                    type='number'
                                    fieldState={form.version}
                                    id='version'
                                    label={languageStore.get('version')} />

                                <Dropdown
                                    fieldState={form.status}
                                    id='status'
                                    label={languageStore.get('status')}
                                    type='number'
                                    options={modalStatusOptions} />

                                <Textarea
                                    fieldState={form.template}
                                    id='template'
                                    label={languageStore.get('promptFieldLabel')} />

                                <div className='mb-8 rounded border p-4 bg-grey-lightest'>
                                    <div className='font-semibold mb-2'>{languageStore.get('companyIdentityVariables')}</div>
                                    <p className='text-sm text-grey-darker mb-3'>
                                        {languageStore.get('promptPlaceholderHelpText')}
                                    </p>
                                    <p className='text-sm text-grey-darker mb-2'>
                                        {languageStore.get('promptPlaceholderSingularHelpText')}
                                    </p>
                                    <p className='text-sm text-grey-darker mb-4'>
                                        {languageStore.get('promptPlaceholderPluralHelpText')}
                                    </p>

                                    <div className='mb-4'>
                                        {
                                            supportedPromptPlaceholders.map((placeholder) => (
                                                <button
                                                    key={placeholder}
                                                    type='button'
                                                    onClick={() => this.insertTemplatePlaceholder(placeholder)}
                                                    className='border rounded px-3 py-1 mr-2 mb-2 hover:bg-white'>
                                                    {placeholder}
                                                </button>
                                            ))
                                        }
                                    </div>

                                    <div className='text-xs text-grey-darker'>
                                        {languageStore.get('promptPlaceholderInsertHelpText')}
                                    </div>
                                </div>

                                <div className='mb-8 rounded border p-4 bg-grey-lightest'>
                                    <div className='font-semibold mb-2'>{languageStore.get('previousStepVariables')}</div>
                                    <p className='text-sm text-grey-darker mb-3'>
                                        {languageStore.get('previousStepVariablesHelpText')}
                                    </p>
                                    <p className='text-sm text-grey-darker mb-3'>
                                        {languageStore.get('previousStepVariablesValidationHelpText')}
                                    </p>

                                    <div className='font-semibold text-sm mb-2'>
                                        {languageStore.get('fullPreviousResponse')}
                                    </div>
                                    <p className='text-sm text-grey-darker mb-3'>
                                        {languageStore.get('fullPreviousResponseHelpText')}
                                    </p>
                                    {
                                        previousStepResponsePlaceholders.length > 0
                                            ? (
                                                <div className='mb-4'>
                                                    {
                                                        previousStepResponsePlaceholders.map((placeholder) => (
                                                            <button
                                                                key={placeholder}
                                                                type='button'
                                                                onClick={() => this.insertTemplatePlaceholder(placeholder)}
                                                                className='border rounded px-3 py-1 mr-2 mb-2 hover:bg-white'>
                                                                {placeholder}
                                                            </button>
                                                        ))
                                                    }
                                                </div>
                                            )
                                            : (
                                                <div className='text-sm text-grey-darker mb-4'>
                                                    {languageStore.get('noPreviousStepVariablesAvailable')}
                                                </div>
                                            )
                                    }

                                    <div className='font-semibold text-sm mb-2'>
                                        {languageStore.get('previousExtractedFields')}
                                    </div>
                                    <p className='text-sm text-grey-darker mb-3'>
                                        {languageStore.get('previousExtractedFieldsHelpText')}
                                    </p>
                                    <div className='mb-4'>
                                        {
                                            previousExtractedFieldPlaceholders.map((placeholder) => (
                                                <button
                                                    key={placeholder}
                                                    type='button'
                                                    onClick={() => this.insertTemplatePlaceholder(placeholder)}
                                                    className='border rounded px-3 py-1 mr-2 mb-2 hover:bg-white'>
                                                    {placeholder}
                                                </button>
                                            ))
                                        }
                                    </div>

                                    <div className='text-xs text-grey-darker'>
                                        {languageStore.get('promptPlaceholderInsertHelpText')}
                                    </div>
                                </div>
                            </ModalColumn>
                        </Modal>
                    </Form>
                }

                {
                    this.state.promptPendingActivation &&
                    <Modal
                        title={languageStore.get('reviewPromptActivation')}
                        forceWide={true}
                        primaryButton={{ title: languageStore.get('activate'), onClick: this.onConfirmActivate }}
                        secondaryButton={{ title: languageStore.get('cancel'), onClick: this.hideActivateModal }}>

                        <ModalColumn>
                            {
                                activationPrompt &&
                                <div>
                                    <p className='mb-4'>{languageStore.get('activatePromptReviewText')}</p>
                                    <div className='mb-4 text-sm text-grey-darker'>
                                        {`${activationPrompt.flowKey} · ${this.getProviderName(activationPrompt.provider)} · ${languageStore.get('stepOrder')} ${activationPrompt.stepOrder}`}
                                    </div>

                                    <div className='mb-4 border rounded p-4 bg-grey-lightest'>
                                        <div className='font-semibold mb-2'>{languageStore.get('promptToActivate')}</div>
                                        <div>{`v${activationPrompt.version} ${activationPrompt.name}`}</div>
                                    </div>

                                    {
                                        currentActivePrompt
                                            ? (
                                                <div className='mb-4 border rounded p-4 bg-yellow-lightest'>
                                                    <div className='font-semibold mb-2'>{languageStore.get('currentlyActivePrompt')}</div>
                                                    <div>{`v${currentActivePrompt.version} ${currentActivePrompt.name}`}</div>
                                                </div>
                                            )
                                            : (
                                                <div className='mb-4 border rounded p-4 bg-red-lightest text-red-dark'>
                                                    {languageStore.get('noCurrentActivePrompt')}
                                                </div>
                                            )
                                    }

                                    {
                                        activationComparisonRows.length > 0 &&
                                        <div className='mb-4'>
                                            <div className='font-semibold mb-2'>{languageStore.get('changedFields')}</div>
                                            {
                                                activationComparisonRows.map((row) => (
                                                    <div key={row.label} className='border rounded p-3 mb-2'>
                                                        <div className='font-semibold mb-2'>{row.label}</div>
                                                        <div className='text-sm text-grey-darker mb-1'>
                                                            {`${languageStore.get('currentValue')}: ${row.currentValue}`}
                                                        </div>
                                                        <div className='text-sm'>
                                                            {`${languageStore.get('newValue')}: ${row.nextValue}`}
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    }

                                    {
                                        currentActivePrompt &&
                                        <div className='mb-4'>
                                            <div className='font-semibold mb-2'>{languageStore.get('currentActivePromptContent')}</div>
                                            <div className='border rounded p-3 bg-grey-lightest text-xs whitespace-pre-wrap'>
                                                {currentActivePrompt.template}
                                            </div>
                                        </div>
                                    }

                                    <div>
                                        <div className='font-semibold mb-2'>{languageStore.get('newPromptContent')}</div>
                                        <div className='border rounded p-3 bg-grey-lightest text-xs whitespace-pre-wrap'>
                                            {activationPrompt.template}
                                        </div>
                                    </div>
                                </div>
                            }
                        </ModalColumn>
                    </Modal>
                }

                {
                    this.state.promptPendingHardDelete &&
                    <Modal
                        title={languageStore.get('deletePermanently')}
                        primaryButton={{ title: languageStore.get('delete'), onClick: this.onHardDelete }}
                        secondaryButton={{ title: languageStore.get('cancel'), onClick: this.hideHardDeleteModal }}>

                        <ModalColumn>
                            <p>{languageStore.get('permanentlyDeletePromptConfirmation')}</p>
                        </ModalColumn>
                        </Modal>
                }

                {
                    this.state.showBulkHardDeleteModal &&
                    <Form formState={this.bulkHardDeleteForm} onValidSubmit={this.onHardDeleteAllArchived}>
                        <Modal
                            title={languageStore.get('deleteAllArchivedPermanently')}
                            primaryButton={{ title: languageStore.get('delete') }}
                            secondaryButton={{ title: languageStore.get('cancel'), onClick: this.closeBulkHardDeleteModal }}>

                            <ModalColumn>
                                <p className='mb-4'>{languageStore.get('permanentlyDeleteAllArchivedPromptsConfirmation')}</p>
                                <p>{`${languageStore.get('typeToConfirmDeleteAllArchivedPrompts')} ${this.bulkHardDeletePhrase}`}</p>
                                <div className='mt-12' />
                                <Input
                                    fieldState={this.bulkHardDeleteForm.$.confirmKey}
                                    label={languageStore.get('confirmationPhrase')}
                                    id='bulkHardDeleteConfirmKey'
                                />
                            </ModalColumn>
                        </Modal>
                    </Form>
                }

                <button
                    onClick={this.openInfoModal}
                    className='fixed pin-b pin-r mb-6 mr-6 bg-comablue-label text-white px-5 py-3 rounded-full shadow-lg'>
                    {languageStore.get('promptInfo')}
                </button>

                {
                    this.state.showInfoModal &&
                    <Modal
                        title={languageStore.get('aiPromptGuideTitle')}
                        forceWide={true}
                        secondaryButton={{ title: languageStore.get('close'), onClick: this.closeInfoModal }}>

                        <ModalColumn>
                            <p className='mb-4'>{languageStore.get('aiPromptGuideIntro')}</p>
                            <p className='mb-4'>{languageStore.get('aiPromptGuideGrouping')}</p>
                            <p className='mb-4'>{languageStore.get('aiPromptGuideActivation')}</p>
                            <p className='mb-4'>{languageStore.get('aiPromptGuideEditing')}</p>
                            <p>{languageStore.get('aiPromptGuideExecution')}</p>
                        </ModalColumn>
                    </Modal>
                }
            </div>
        );
    }
}

export default AiPromptsPage;
